/* eslint-disable no-console */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  getDoc,
  addDoc,
  query,
  where,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../firebase";

/**
 * EditTeams
 * Handles:
 *  - For "Teams" or for "Doubles" (Individuals), you can assign or remove members.
 *  - Removing a real member => references in Matches/Draws become fill-in.
 *  - Replacing fill-in placeholders with real members.
 *  - Optionally regenerating all teams (Teams mode).
 *  - In Doubles mode (participantType=Individuals + playType=Doubles), you can
 *    create a new "team" doc with exactly 2 selected members (LASTNAME1/LASTNAME2).
 */
export default function EditTeams({
  isOpen,
  toggle,
  pennantId,
  refreshTeams,
  onRegenerateTeams,
}) {
  const [pennantData, setPennantData] = useState(null);
  const [pennantSections, setPennantSections] = useState({});
  const [members, setMembers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [firstSelectedSection, setFirstSelectedSection] = useState(null);

  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [confirmRegenerateOpen, setConfirmRegenerateOpen] = useState(false);



  useEffect(() => {
    if (!pennantId) return;

    const fetchData = async () => {
      try {
        const pennantDocRef = doc(db, "Pennants", pennantId);
        const pennantDocSnap = await getDoc(pennantDocRef);
        if (pennantDocSnap.exists()) {
          const pData = pennantDocSnap.data();
          setPennantData(pData);
          setPennantSections(pData.members || {});
        } else {
          console.error("No such Pennant exists.");
        }

        const membersSnapshot = await getDocs(collection(db, "members"));
        const membersList = membersSnapshot.docs.map((m) => ({
          id: m.id,
          ...m.data(),
        }));
        setMembers(membersList);

        const teamsSnapshot = await getDocs(
          collection(db, "Pennants", pennantId, "teams")
        );
        const teamsList = teamsSnapshot.docs.map((t) => ({
          id: t.id,
          ...t.data(),
        }));
        setTeams(teamsList);
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };

    fetchData();
  }, [pennantId]);

  const isDoublesMode =
    pennantData?.participantType === "Individuals" &&
    pennantData?.playType === "Doubles";

  const getAssignedMemberIds = () =>
    new Set(
      teams.flatMap((team) => {
        if (!team.members) return [];
        return Object.values(team.members).map((obj) => obj.id);
      })
    );

  const unassignedMembers = members.filter(
    (m) =>
      Object.values(pennantSections).flat().includes(m.id) &&
      !getAssignedMemberIds().has(m.id)
  );

  // eslint-disable-next-line arrow-body-style
  const getMemberSection = (memberId) => {
    return Object.keys(pennantSections).find((sec) =>
      pennantSections[sec]?.includes(memberId)
    );
  };

  const handleSelectMember = (memberId) => {
    if (!isDoublesMode) {
      setSelectedMembers((prev) =>
        prev.includes(memberId)
          ? prev.filter((id) => id !== memberId)
          : [...prev, memberId]
      );
      return;
    }

    const alreadySelected = selectedMembers.includes(memberId);
    const memberSection = getMemberSection(memberId);

    if (alreadySelected) {
      setSelectedMembers((prev) => prev.filter((id) => id !== memberId));
      if (memberSection === firstSelectedSection && selectedMembers.length === 1) {
        setFirstSelectedSection(null);
      }
      return;
    }

    if (selectedMembers.length >= 2) {
      alert("You have already selected 2 members for a doubles pair.");
      return;
    }

    if (selectedMembers.length === 0) {
      setSelectedMembers([memberId]);
      setFirstSelectedSection(memberSection);
      return;
    }

    if (selectedMembers.length === 1) {
      if (memberSection !== firstSelectedSection) {
        alert(
          `Cannot select ${memberId} from section ${memberSection} ` +
            `when the first selected member is from section ${firstSelectedSection}.`
        );
        return;
      }
      setSelectedMembers((prev) => [...prev, memberId]);
    }
  };

  async function replaceMemberInFutureMatches(removedMemberId, fillInId) {
    try {
      const qMatches = query(
        collection(db, "Matches"),
        where("pennantId", "==", pennantId)
      );
      const snapshot = await getDocs(qMatches);

      const updatePromises = [];
      snapshot.forEach((matchDoc) => {
        const matchData = matchDoc.data();
        const { team1, team2 } = matchData;
        let updated = false;
        const newMatchData = { ...matchData };

        if (team1?.playerIds?.includes(removedMemberId)) {
          newMatchData.team1 = {
            ...team1,
            name: "Fill-In Needed",
            id: fillInId,
            fillIn: true,
            playerIds: [],
          };
          updated = true;
        }
        if (team2?.playerIds?.includes(removedMemberId)) {
          newMatchData.team2 = {
            ...team2,
            name: "Fill-In Needed",
            id: fillInId,
            fillIn: true,
            playerIds: [],
          };
          updated = true;
        }

        if (updated) {
          updatePromises.push(
            updateDoc(doc(db, "Matches", matchDoc.id), newMatchData)
          );
        }
      });

      await Promise.all(updatePromises);
    } catch (err) {
      console.error("Error in replaceMemberInFutureMatches:", err);
    }
  }

  async function replaceMemberInFutureDraws(removedMemberId, fillInId) {
    try {
      const drawsSnap = await getDocs(collection(db, "Pennants", pennantId, "draws"));
      if (drawsSnap.empty) return console.log("No draws found, skipping.");

      const updatePromises = [];
      drawsSnap.forEach((drawDoc) => {
        const drawData = drawDoc.data();
        const { weeks } = drawData;
        if (!weeks || !Array.isArray(weeks)) return;

        let updated = false;
        const updatedWeeks = weeks.map((week) => {
          const newMatches = week.matches.map((m) => {
            const nm = { ...m };
            if (nm.team1?.id === removedMemberId) {
              nm.team1 = {
                ...nm.team1,
                name: "Fill-In Needed",
                id: fillInId,
                fillIn: true,
              };
              updated = true;
            }
            if (nm.team2?.id === removedMemberId) {
              nm.team2 = {
                ...nm.team2,
                name: "Fill-In Needed",
                id: fillInId,
                fillIn: true,
              };
              updated = true;
            }
            return nm;
          });
          return { ...week, matches: newMatches };
        });

        if (updated) {
          updatePromises.push(
            updateDoc(doc(db, "Pennants", pennantId, "draws", drawDoc.id), {
              weeks: updatedWeeks,
            })
          );
        }
      });

      await Promise.all(updatePromises);
    } catch (err) {
      console.error("Error in replaceMemberInFutureDraws:", err);
    }
  }

  const handleRemoveMember = async (teamId, memberId) => {
    const tUp = teams.find((t) => t.id === teamId);
    if (!tUp) {
      console.error("Team not found for remove ID:", teamId);
      return;
    }

    const updatedMembers = { ...tUp.members };
    const foundKey = Object.entries(updatedMembers).find(
      ([_, obj]) => obj.id === memberId
    )?.[0];
    if (foundKey) {
      delete updatedMembers[foundKey];
    }

    try {
      const teamDocRef = doc(db, "Pennants", pennantId, "teams", teamId);
      await updateDoc(teamDocRef, { members: updatedMembers });

      setTeams((prev) =>
        prev.map((tt) =>
          tt.id === teamId ? { ...tt, members: updatedMembers } : tt
        )
      );

      const newFillInId = `fillIn-${Date.now()}-${Math.floor(
        Math.random() * 10000
      )}`;

      await replaceMemberInFutureMatches(memberId, newFillInId);
      await replaceMemberInFutureDraws(memberId, newFillInId);

      refreshTeams();
    } catch (err) {
      console.error("Error removing member from team:", err);
    }
  };

  const promptRemoveMember = (teamId, memberId) => {
    const tUp = teams.find((t) => t.id === teamId);
    if (!tUp) return;
    const entry = Object.entries(tUp.members || {}).find(
      ([_, obj]) => obj.id === memberId
    );
    if (!entry) return;

    const [sectionKey, removedMemberObj] = entry;
    const actualSection = removedMemberObj.section || sectionKey;
    setRemoveTarget({ teamId, memberId, sectionKey, actualSection });
    setRemoveDialogOpen(true);
  };

  const handleAddFillInRequirement = async (actualSection, teamId) => {
    try {
      await addDoc(collection(db, "Pennants", pennantId, "fillInRequired"), {
        createdAt: new Date(),
        section: actualSection,
        reason: "Member removed",
      });

      const teamDocRef = doc(db, "Pennants", pennantId, "teams", teamId);
      const snap = await getDoc(teamDocRef);
      if (!snap.exists()) return;

      const existingData = snap.data() || {};
      const updatedMembers = { ...(existingData.members || {}) };

      const fillInId = `fillIn-${Date.now()}-${Math.floor(
        Math.random() * 10000
      )}`;
      updatedMembers[actualSection] = {
        id: fillInId,
        firstName: "Fill-In",
        lastName: "Needed",
        fillIn: true,
        section: actualSection,
      };

      await updateDoc(teamDocRef, { members: updatedMembers });

      setTeams((prev) =>
        prev.map((tt) =>
          tt.id === teamId ? { ...tt, members: updatedMembers } : tt
        )
      );
    } catch (err) {
      console.error("Error creating fill-in doc or placeholder:", err);
    }
  };

  const handleRemoveDialogChoice = async (choice) => {
    setRemoveDialogOpen(false);
    if (!removeTarget) return;

    const { teamId, memberId, actualSection } = removeTarget;
    await handleRemoveMember(teamId, memberId);

    if (choice === "fillIn") {
      await handleAddFillInRequirement(actualSection, teamId);
    }

    setRemoveTarget(null);
  };

  const handleAssignMembers = async (teamId) => {
    const teamToUpdate = teams.find((t) => t.id === teamId);
    if (!teamToUpdate) return console.error("Team not found ID:", teamId);

    const updatedMembers = { ...(teamToUpdate.members || {}) };
    selectedMembers.forEach((memberId) => {
      const mem = members.find((m) => m.id === memberId);
      if (!mem) return;
      const foundSection = Object.keys(pennantSections).find((sec) =>
        pennantSections[sec]?.includes(mem.id)
      );
      if (foundSection) {
        updatedMembers[foundSection] = {
          id: mem.id,
          firstName: mem.firstName,
          lastName: mem.lastName,
          pennantRanking: mem.pennantRanking || "NA",
          section: foundSection,
        };
      }
    });

    try {
      const teamDocRef = doc(db, "Pennants", pennantId, "teams", teamId);
      await updateDoc(teamDocRef, { members: updatedMembers });

      setTeams((prev) =>
        prev.map((tm) =>
          tm.id === teamId ? { ...tm, members: updatedMembers } : tm
        )
      );
      setSelectedMembers([]);
      refreshTeams();
    } catch (err) {
      console.error("Error assigning members:", err);
    }
  };


  async function replaceFillInInMatches(fillInId, memObj) {
    try {
      const qMatches = query(
        collection(db, "Matches"),
        where("pennantId", "==", pennantId)
      );
      const snapshot = await getDocs(qMatches);
      const updatePromises = [];

      snapshot.forEach((matchDoc) => {
        const mData = matchDoc.data();
        const { team1, team2 } = mData;
        let updated = false;
        const newData = { ...mData };

        if (team1?.id === fillInId) {
          newData.team1 = {
            ...team1,
            id: memObj.id,
            name: `${memObj.firstName} ${memObj.lastName}`,
            fillIn: false,
            playerIds: [memObj.id],
          };
          updated = true;
        }
        if (team2?.id === fillInId) {
          newData.team2 = {
            ...team2,
            id: memObj.id,
            name: `${memObj.firstName} ${memObj.lastName}`,
            fillIn: false,
            playerIds: [memObj.id],
          };
          updated = true;
        }
        if (updated) {
          updatePromises.push(
            updateDoc(doc(db, "Matches", matchDoc.id), newData)
          );
        }
      });

      await Promise.all(updatePromises);
    } catch (err) {
      console.error("Error replacing fill-in in matches:", err);
    }
  }

  async function replaceFillInInDraws(fillInId, memObj) {
    try {
      const drawsSnap = await getDocs(collection(db, "Pennants", pennantId, "draws"));
      if (drawsSnap.empty) return console.log("No draws found, skip.");

      const updatePromises = [];
      drawsSnap.forEach((drawDoc) => {
        const dData = drawDoc.data();
        const { weeks } = dData;
        if (!Array.isArray(weeks)) return;

        let changed = false;
        const updatedWeeks = weeks.map((wk) => {
          const newMatches = wk.matches.map((m) => {
            const nm = { ...m };
            if (nm.team1?.id === fillInId) {
              nm.team1 = {
                ...nm.team1,
                id: memObj.id,
                name: `${memObj.firstName} ${memObj.lastName}`,
                fillIn: false,
              };
              changed = true;
            }
            if (nm.team2?.id === fillInId) {
              nm.team2 = {
                ...nm.team2,
                id: memObj.id,
                name: `${memObj.firstName} ${memObj.lastName}`,
                fillIn: false,
              };
              changed = true;
            }
            return nm;
          });
          return { ...wk, matches: newMatches };
        });

        if (changed) {
          updatePromises.push(
            updateDoc(doc(db, "Pennants", pennantId, "draws", drawDoc.id), {
              weeks: updatedWeeks,
            })
          );
        }
      });

      await Promise.all(updatePromises);
    } catch (err) {
      console.error("Error replacing fill-in in draws:", err);
    }
  }

  const handleReplaceFillIn = async (teamId, fillInId, fillInSection) => {
    if (selectedMembers.length !== 1) {
      alert("Select exactly 1 unassigned member to replace this fill-in.");
      return;
    }

    const memberId = selectedMembers[0];
    const memObj = members.find((m) => m.id === memberId);
    if (!memObj) return alert("Selected member not found in local data.");

    const tm = teams.find((t) => t.id === teamId);
    if (!tm) return alert("Team not found in local data.");

    const updatedMembers = { ...tm.members };
    const existingFillIn = updatedMembers[fillInSection];
    if (!existingFillIn || existingFillIn.id !== fillInId) {
      alert("No matching fill-in placeholder found in that section.");
      return;
    }

    updatedMembers[fillInSection] = {
      id: memObj.id,
      firstName: memObj.firstName,
      lastName: memObj.lastName,
      fillIn: false,
      section: fillInSection,
      pennantRanking: memObj.pennantRanking || "NA",
    };

    try {
      const teamDocRef = doc(db, "Pennants", pennantId, "teams", teamId);
      await updateDoc(teamDocRef, { members: updatedMembers });

      setTeams((prev) =>
        prev.map((tt) =>
          tt.id === teamId ? { ...tt, members: updatedMembers } : tt
        )
      );

      await replaceFillInInMatches(fillInId, memObj);
      await replaceFillInInDraws(fillInId, memObj);

      setSelectedMembers([]);
      refreshTeams();
    } catch (err) {
      console.error("Error replacing fill-in in doc:", err);
    }
  };


  const handleCreateNewDoublesPair = async () => {
    if (selectedMembers.length !== 2) {
      alert("Please select exactly 2 unassigned members to form a doubles pair.");
      return;
    }

    const [m1Id, m2Id] = selectedMembers;
    const mem1 = members.find((m) => m.id === m1Id);
    const mem2 = members.find((m) => m.id === m2Id);
    if (!mem1 || !mem2) {
      alert("Could not find one or both selected members in local data.");
      return;
    }

    const mem1Sec = getMemberSection(m1Id);
    const mem2Sec = getMemberSection(m2Id);
    if (!mem1Sec || !mem2Sec || mem1Sec !== mem2Sec) {
      alert(
        `Cannot form a doubles pair from different sections:\n` +
          `${mem1.firstName} => ${mem1Sec}\n` +
          `${mem2.firstName} => ${mem2Sec}\n\n` +
          `They must be in the same section.`
      );
      return;
    }

    const name = `${mem1.lastName || "Member1"}/${mem2.lastName || "Member2"}`;

    const newMembersObj = {
      "1": {
        id: mem1.id,
        firstName: mem1.firstName,
        lastName: mem1.lastName,
        fillIn: false,
        section: mem1Sec,
        pennantRanking: mem1.pennantRanking || "NA",
      },
      "2": {
        id: mem2.id,
        firstName: mem2.firstName,
        lastName: mem2.lastName,
        fillIn: false,
        section: mem2Sec,
        pennantRanking: mem2.pennantRanking || "NA",
      },
    };

    try {
      const teamsColRef = collection(db, "Pennants", pennantId, "teams");
      await addDoc(teamsColRef, { name, members: newMembersObj });

      setSelectedMembers([]);
      setFirstSelectedSection(null);

      await refreshTeams();
      alert(`Created new Doubles pair: ${name}`);
    } catch (err) {
      console.error("Error creating new doubles pair:", err);
      alert("Failed to create doubles pair. See console.");
    }
  };

  const handleConfirmRegenerate = async () => {
    setConfirmRegenerateOpen(false);
    if (onRegenerateTeams) {
      await onRegenerateTeams();
    }
  };

  const handleEditTeamName = async (teamId, newName) => {
    try {
      const teamDocRef = doc(db, "Pennants", pennantId, "teams", teamId);
      await updateDoc(teamDocRef, { name: newName });
      setTeams((prev) =>
        prev.map((t) => (t.id === teamId ? { ...t, name: newName } : t))
      );
    } catch (err) {
      console.error("Error updating team name:", err);
    }
  };

  return (
    <Dialog open={isOpen} onClose={toggle} maxWidth="lg" fullWidth>
      <Container>
        <Typography variant="h4" gutterBottom>
          Edit Teams
        </Typography>

        {isDoublesMode && (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleCreateNewDoublesPair}
              disabled={selectedMembers.length < 2}
            >
              Create New Doubles Pair
            </Button>
          </Box>
        )}

        <Box display="flex" justifyContent="space-between" sx={{ mt: 3 }}>
          <Paper elevation={3} sx={{ flex: 1, mr: 2, p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Unassigned Members
            </Typography>
            <List>
              {unassignedMembers.map((m) => {
                const section = getMemberSection(m.id);
                const checked = selectedMembers.includes(m.id);

                let isDisabled = false;

                if (isDoublesMode) {
                  if (selectedMembers.length >= 2 && !checked) {
                    isDisabled = true;
                  }
                  else if (
                    selectedMembers.length === 1 &&
                    !checked &&
                    firstSelectedSection != null &&
                    section !== firstSelectedSection
                  ) {
                    isDisabled = true;
                  }
                }

                return (
                  <ListItem key={m.id} button>
                    <Checkbox
                      checked={checked}
                      onChange={() => handleSelectMember(m.id)}
                      disabled={isDisabled}
                    />
                    <ListItemText
                      primary={`${m.firstName} ${m.lastName}`}
                      secondary={`Section: ${section || "N/A"}, Rank: ${
                        m.pennantRanking || "N/A"
                      }`}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Paper>

          <Paper elevation={3} sx={{ flex: 2, ml: 2, p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Teams
            </Typography>

            {pennantData?.participantType === "Teams" && (
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => setConfirmRegenerateOpen(true)}
                >
                  Regenerate Teams
                </Button>
              </Box>
            )}

            {teams.map((team) => (
              <Box key={team.id} sx={{ mb: 3 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <TextField
                    value={team.name || `Team ${team.id}`}
                    onChange={(e) => handleEditTeamName(team.id, e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ width: "70%" }}
                  />

                  <Button
                    variant="contained"
                    size="small"
                    disabled={selectedMembers.length === 0}
                    onClick={() => handleAssignMembers(team.id)}
                  >
                    Assign Selected
                  </Button>
                </Box>

                {team.members && typeof team.members === "object" ? (
                  <List>
                    {Object.entries(team.members).map(([sec, memObj]) => {
                      const isFillIn = memObj.fillIn === true;
                      return (
                        <ListItem key={memObj.id}>
                          <ListItemText
                            primary={`${memObj.firstName} ${memObj.lastName}${
                              isFillIn ? " (Fill-In)" : ""
                            }`}
                            secondary={`Section: ${
                              memObj.section || sec
                            }, Rank: ${memObj.pennantRanking || "N/A"}`}
                          />
                          <ListItemSecondaryAction>
                            {isFillIn ? (
                              <Button
                                variant="contained"
                                size="small"
                                color="success"
                                onClick={() =>
                                  handleReplaceFillIn(team.id, memObj.id, sec)
                                }
                                disabled={selectedMembers.length !== 1}
                              >
                                Assign Selected
                              </Button>
                            ) : (
                              <Button
                                color="error"
                                onClick={() =>
                                  promptRemoveMember(team.id, memObj.id)
                                }
                              >
                                Remove
                              </Button>
                            )}
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                  </List>
                ) : (
                  <Typography variant="body2">
                    No members found on this team
                  </Typography>
                )}
              </Box>
            ))}
          </Paper>
        </Box>

        <Button variant="outlined" onClick={toggle} sx={{ mt: 3 }}>
          Close
        </Button>
      </Container>

      <Dialog open={removeDialogOpen} onClose={() => setRemoveDialogOpen(false)}>
        <DialogTitle>Remove Member</DialogTitle>
        <DialogContent>
          <Typography>
            Do you want to remove this member only, or also add a fill-in request
            for their section?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleRemoveDialogChoice("remove")}
            variant="contained"
            color="error"
          >
            Remove Only
          </Button>
          <Button
            onClick={() => handleRemoveDialogChoice("fillIn")}
            variant="contained"
            color="primary"
          >
            Remove &amp; Add Fill-In
          </Button>
          <Button
            onClick={() => setRemoveDialogOpen(false)}
            variant="outlined"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmRegenerateOpen}
        onClose={() => setConfirmRegenerateOpen(false)}
      >
        <DialogTitle>Regenerate Teams?</DialogTitle>
        <DialogContent>
          <Typography>
            Regenerating teams will delete existing teams and create new ones
            (with fill-ins if needed). This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setConfirmRegenerateOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmRegenerate}
          >
            Yes, Regenerate
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

EditTeams.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  pennantId: PropTypes.string.isRequired,
  refreshTeams: PropTypes.func.isRequired,
  onRegenerateTeams: PropTypes.func,
};
